import React from 'react';
import useRetry, { RESULTS } from '../../../hooks/useRetry';
import { ImageModifier } from './Image.modifier';
import { Image as ImageType, isContentImage } from '../../../types/api/ImageType';
import { BREAKPOINT_TO_IMAGE_WIDTH } from './Responsive/BreakpointToSizes';
import { MediaInformation } from '../../organisms/MediaInformation';
import layoutVariations from '../../blocks/LayoutGroup/layoutVariations';
import { useImage } from './useImage';
import { ImageLoadingSpinner } from './partials/ImageLoadingSpinner';
import { Img } from './partials/Img';
import { ImgOrPicture } from './partials/ImgOrPicture';
import { PortraitProfileImage } from './partials/PortraitProfileImage';
import { LinkedImage } from './partials/LinkedImage';
import { AriaHiddenImage } from './partials/AriaHiddenImage';
import { useSonTranslation } from '../../../translation/useSonTranslation';

// extracts the value of the 'type' property
//                                 if non-empty readonly Array
type GetResponsiveTypes<TIn> = TIn extends readonly [infer Element, ...infer Rest]
// if Element has type-property
  ? Element extends { type: infer Type }
  // if Type is string
    ? Type extends string
      ? Type | GetResponsiveTypes<Rest> // add type to union and append the rest
      : never // Type is not a string
    : never // Element has no 'type' property
  : never; // is not a non-empty readonly Array

export type ResponsiveType = GetResponsiveTypes<typeof BREAKPOINT_TO_IMAGE_WIDTH>;

interface ImageProps {
  image: ImageType;
  modifier?: ImageModifier;
  emptyAltText?: boolean;
  customAltText?: string;
  linkUrl?: string;
  /*
      Need this prop and corresponding code to avoid
      "stutter" for screen reader users when combining
      adjacent image and text links for the same resource.
      (see https://www.w3.org/WAI/WCAG21/Techniques/html/H2)
       */
  hideAdjacentImageLink?: boolean;
  responsiveType?: ResponsiveType;
  lazyLoading?: boolean;
  fetchUrl?: string;
  groupLayout?: string;
  hasPopUp?: boolean;
  onClickForPopUp?: () => void;
  isImageInPopUpSlider?: boolean;
}

export const fallbackWidth: number = 1024;

export function Image({
  modifier,
  image: originalImage,
  emptyAltText,
  customAltText,
  linkUrl,
  hideAdjacentImageLink,
  responsiveType,
  lazyLoading,
  fetchUrl,
  groupLayout,
  hasPopUp,
  onClickForPopUp,
  isImageInPopUpSlider,
}: ImageProps) {
  const { t } = useSonTranslation('Image');
  /**
   * Sometimes images aren't readily processed in backend in the moment we want to render them
   * (mostly refetch after image upload).
   * When backend sends pending state here, we show a loading spinner instead of the image
   * until image is ready.
   */
  const { result, image } = useRetry({
    image: originalImage,
    url: fetchUrl,
    maxRetries: 5,
    msBetweenRetries: 3000,
  });

  const { hasCopyright, getAlt, getWidthWithQueryParameter, hasPopUpButton, getAvailableWidthOfImage } = useImage(
    image,
    emptyAltText,
    customAltText,
    hasPopUp,
  );

  if (result === RESULTS.pending) {
    return <ImageLoadingSpinner />;
  }

  /**
   * If there is no image data, return nothing.
   */
  if (!image) {
    return null;
  }

  /**
   * Here we decide, if image is wrapped in mediaInformation
   * (depending on copyright)
   */
  if (hasCopyright(image)) {
    return (
      <AriaHiddenImage hideAdjacentImageLink={hideAdjacentImageLink}>
        <MediaInformation
          media={{
            copyright: image.copyright,
            caption: isContentImage(image) ? image.caption : undefined,
            alt: getAlt(),
            size: isContentImage(image) ? image.size : undefined,
            imageType: image.imageType,
            contentType: 'image',
          }}
          groupLayout={groupLayout as keyof typeof layoutVariations}
          hasPopUp={hasPopUpButton()}
          popUpContent={
            (isContentImage(image) ? image.popUp : false)
              ? (
                  <a
                    href={image.src}
                    // overwriting the alt for screen readers for more exact link name
                    aria-label={t('imageInOriginalSize', { alt: getAlt() })}
                    className="image__link-to-original"
                  >
                    <Img
                      image={image}
                      modifier={modifier ? [modifier, ImageModifier.POP_UP] : ImageModifier.POP_UP}
                      lazyLoading={lazyLoading}
                      getWidthWithQueryParameter={getWidthWithQueryParameter}
                      getAlt={getAlt}
                    />
                  </a>
                )
              : undefined
          }
          slider={modifier === ImageModifier.SLIDER}
          onClickForPopUp={onClickForPopUp}
          isImageInPopUpSlider={isImageInPopUpSlider}
        >
          <LinkedImage linkUrl={linkUrl} hideAdjacentImageLink={hideAdjacentImageLink}>
            <PortraitProfileImage image={image}>
              <ImgOrPicture
                image={image}
                modifier={modifier}
                lazyLoading={lazyLoading}
                responsiveType={responsiveType}
                getAlt={getAlt}
                getWidthWithQueryParameter={getWidthWithQueryParameter}
                getAvailableWidthOfImage={getAvailableWidthOfImage}
              />
            </PortraitProfileImage>
          </LinkedImage>
        </MediaInformation>
      </AriaHiddenImage>
    );
  }

  return (
    <LinkedImage linkUrl={linkUrl} hideAdjacentImageLink={hideAdjacentImageLink}>
      <PortraitProfileImage image={image}>
        <ImgOrPicture
          image={image}
          modifier={modifier}
          lazyLoading={lazyLoading}
          responsiveType={responsiveType}
          getAlt={getAlt}
          getWidthWithQueryParameter={getWidthWithQueryParameter}
          getAvailableWidthOfImage={getAvailableWidthOfImage}
        />
      </PortraitProfileImage>
    </LinkedImage>
  );
}
